import ControlFactory from "@/Factories/ControlFactory";
import WebComponentControl, {
    IWebComponentControlConstructorParameters
} from "@/Components/WebComponents/WebComponentControl";
import WebComponentControlViewModel, {
    IWebComponentControlViewModelConstructorParameters
} from "./WebComponentControlViewModel";
import ComponentManager from "@/Management/ComponentManager";
import {
    WebComponentBindingDependencies,
    WebComponentBindingOptions,
    WebComponentBindingProperties,
} from "#/CoreFrameInterfaces";

type Constructor<T> = new (...args: any[]) => T;

export default abstract class WebComponentControlFactory<
    TOriginalComponent = unknown,
    V extends WebComponentControlViewModel = WebComponentControlViewModel,
    C extends WebComponentControl<TOriginalComponent> = WebComponentControl<TOriginalComponent>
> extends ControlFactory {
    /**
     * @override
     */
    public viewModel: V;

    /**
     * @override
     */
    public control: C;

    protected get viewModelName(): string {
        return "WebComponentControl";
    }

    protected get controlName(): string {
        return "WebComponentControl";
    }

    protected abstract get tagName(): string;

    /**
     * @override
     */
    public getControl(): JQuery<HTMLElement> {
        if (!this.viewModel)
            this.viewModel = this.createViewModel();

        if (!this.control)
            this.control = this.createControl(this.viewModel);

        const tuple = this.control.getTuple();
        return tuple.control;
    }

    protected createViewModel(): V {
        const ViewModel = ComponentManager.getViewModel<Constructor<V>>(this.viewModelName);
        return new ViewModel(this.createViewModelParameters());
    }

    protected createViewModelParameters(): IWebComponentControlViewModelConstructorParameters {
        return {};
    }

    protected createControl(viewModel: V): C {
        const Control = ComponentManager.getControl<Constructor<C>>(this.controlName);
        return new Control(this.createControlParameters(), viewModel);
    }

    protected createControlParameters(): IWebComponentControlConstructorParameters<TOriginalComponent> {
        return {
            tagName: this.tagName,
            bindingOptions: this.createBindingOptions(),
        }
    }

    private createBindingOptions(): WebComponentBindingOptions<TOriginalComponent> {
        return {
            dependencies: this.getDependencies(),
            properties: this.getProperties(),
        };
    }

    protected getDependencies(): WebComponentBindingDependencies {
        return {};
    }

    protected getProperties(): WebComponentBindingProperties<TOriginalComponent> {
        return {};
    }
}